import { msalInstance } from '../components/AuthProvider';
import { getAzureConfig } from '../utils/envUtils';
import { DirectTokenService } from './DirectTokenService';
import { AxiosInstance } from 'axios';
import { InteractionRequiredAuthError, BrowserAuthError, AuthError } from '@azure/msal-browser';
import { extractErrorMessage } from '../utils/apiUtils';
import { stashHashRouteForMsalRedirect } from '../utils/msalRedirectUtils';
import { logMsalConfigDiagnostics } from '../utils/envUtils';
import { debugLog } from '../utils/debugLog';

export class ScimAuthService {
    // Debug flag - set to true to enable verbose authentication logging
    private static readonly DEBUG_AUTH = false;

    /**
     * Get authentication token for API calls
     */
    static async getAuthToken(): Promise<string> {
        logMsalConfigDiagnostics('ScimAuthService.getAuthToken:start', {
            hasDirectToken: DirectTokenService.hasDirectToken(),
            activeAccount: msalInstance.getActiveAccount()?.username ?? null,
            allAccountCount: msalInstance.getAllAccounts().length
        });
        // Check for direct token in localhost/debug mode first
        if (DirectTokenService.hasDirectToken()) {
            const directToken = DirectTokenService.getDirectToken();
            if (directToken) {
                if (this.DEBUG_AUTH) {
                    debugLog('✅ Using direct authentication token (localhost/debug mode)');
                }
                return directToken;
            }
        }

        const azureConfig = getAzureConfig();
        logMsalConfigDiagnostics('ScimAuthService.getAuthToken:azureConfig', {
            scopes: azureConfig.scopes,
            tenantId: azureConfig.tenantId,
            authority: azureConfig.authority,
            domainHint: azureConfig.domainHint,
            azureConfigClientId: azureConfig.clientId,
            azureConfigIsConfigured: azureConfig.isConfigured
        });

        const account = msalInstance.getActiveAccount();
        if (!account) {
            if (this.DEBUG_AUTH) {
                console.warn('⚠️  No active account found, checking for available accounts...');
            }
            const accounts = msalInstance.getAllAccounts();
            if (this.DEBUG_AUTH) {
                debugLog(`📋 Available accounts: ${accounts.length}`);
            }
            if (accounts.length === 0) {
                throw new Error('Authentication required. Please sign in to access real data.');
            }
            if (this.DEBUG_AUTH) {
                debugLog(`🔄 Using first available account: ${accounts[0].username}`);
            }
        } else {
            if (this.DEBUG_AUTH) {
                debugLog(`✅ Active account found: ${account.username}`);
            }
        }

        if (this.DEBUG_AUTH) {
            debugLog(`🔑 Requesting token with scopes: ${azureConfig.scopes.join(', ')}`);
        }

        let tokenResponse;
        try {
            logMsalConfigDiagnostics('ScimAuthService.getAuthToken:acquireTokenSilent', {
                scopes: azureConfig.scopes,
                requestAccount: (account || msalInstance.getAllAccounts()[0])?.username ?? null
            });
            tokenResponse = await msalInstance.acquireTokenSilent({
                scopes: azureConfig.scopes,
                account: account || msalInstance.getAllAccounts()[0]
            });

            if (!tokenResponse.accessToken) {
                throw new Error('Failed to acquire access token.');
            }

            if (this.DEBUG_AUTH) {
                debugLog(`✅ Token acquired successfully for account: ${tokenResponse.account?.username}`);
            }
            logMsalConfigDiagnostics('ScimAuthService.getAuthToken:success', {
                tokenAccount: tokenResponse.account?.username ?? null,
                expiresOn: tokenResponse.expiresOn?.toISOString?.() ?? null
            });
            return tokenResponse.accessToken;
        } catch (tokenError) {
            // Check for specific MSAL error types
            const isInteractionRequired = tokenError instanceof InteractionRequiredAuthError;
            const errorMessage = tokenError instanceof Error ? tokenError.message : String(tokenError);
            const errorStack = tokenError instanceof Error ? tokenError.stack ?? '' : '';

            // Check for timeout or expired refresh token errors
            const isTimeoutError = errorMessage.includes('monitor_window_timeout') ||
                errorMessage.includes('timeout') ||
                errorMessage.includes('TokenRefreshRequired') ||
                errorMessage.includes('refresh_token_expired');

            // Check for interaction required errors
            const requiresInteraction = isInteractionRequired ||
                errorMessage.includes('interaction_required') ||
                errorMessage.includes('consent_required') ||
                errorMessage.includes('login_required');

            // Silent token renewal uses a hidden iframe; when the IdP requires interaction it may try to
            // navigate the top window from inside the iframe, which browsers block (sandbox). Treat as interaction required.
            const isSilentIframeBlocked = /navigation|sandbox|top-level|allow-top-navigation|iframe/i.test(errorMessage) ||
                /navigation|sandbox|top-level|allow-top-navigation/i.test(errorStack);

            logMsalConfigDiagnostics('ScimAuthService.getAuthToken:error', {
                errorMessage,
                isInteractionRequired,
                isTimeoutError,
                requiresInteraction,
                isSilentIframeBlocked
            });

            if (isTimeoutError || requiresInteraction || isSilentIframeBlocked) {
                if (!isSilentIframeBlocked) {
                    console.error('❌ Token acquisition failed:', tokenError);
                } else {
                    console.warn('⚠️ Silent token renewal failed (interaction required). Redirecting to sign-in.');
                }
                // Clear any stale accounts that might have expired refresh tokens
                if (isTimeoutError) {
                    console.warn('⚠️ Token refresh timeout detected. This may indicate an expired refresh token.');
                }

                // Clear the current hash before redirecting to avoid redirect loop
                stashHashRouteForMsalRedirect();

                throw new Error('AUTHENTICATION_REQUIRED');
            }

            // For other errors, throw a generic error
            throw new Error(`Authentication failed: ${errorMessage}`);
        }
    }

    /**
     * Check if user is authenticated
     */
    static isAuthenticated(): boolean {
        // Check for direct token first in localhost/debug mode
        if (DirectTokenService.hasDirectToken()) {
            return true;
        }
        // Check for active account first
        const activeAccount = msalInstance.getActiveAccount();
        if (activeAccount) {
            return true;
        }
        // If no active account, check if there are any available accounts
        const allAccounts = msalInstance.getAllAccounts();
        return allAccounts.length > 0;
    }

    /**
     * Get current account information
     */
    static getCurrentAccount() {
        // If using direct token, return a mock account
        if (DirectTokenService.hasDirectToken()) {
            const username = DirectTokenService.getDirectTokenUsername();
            return username ? {
                username: username,
                name: username,
                localAccountId: 'direct-token',
                environment: 'direct-token'
            } : {
                username: 'direct-token-user',
                name: 'Direct Token User',
                localAccountId: 'direct-token',
                environment: 'direct-token'
            };
        }
        // Try to get active account first
        const activeAccount = msalInstance.getActiveAccount();
        if (activeAccount) {
            return activeAccount;
        }
        // If no active account is set, use the first available account
        const allAccounts = msalInstance.getAllAccounts();
        if (allAccounts.length > 0) {
            return allAccounts[0];
        }
        return null;
    }

    /**
     * Get all available accounts
     */
    static getAllAccounts() {
        return msalInstance.getAllAccounts();
    }

    /**
     * Handle authentication errors and provide user-friendly messages
     */
    static handleAuthError(error: any): Error {
        if (error instanceof Error && error.message.includes('Authentication')) {
            return new Error('Authentication required. Please sign in to access real data.');
        }

        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as any;
            if (this.DEBUG_AUTH) {
                console.error('🌐 Axios error details:', {
                    status: axiosError.response?.status,
                    statusText: axiosError.response?.statusText,
                    data: axiosError.response?.data
                });
            }

            if (axiosError.response?.status === 401) {
                return new Error('Authentication failed. Please sign in again.');
            } else if (axiosError.response?.status === 403) {
                return new Error('Access denied. You do not have permission to access this resource.');
            } else if (axiosError.response?.status >= 500) {
                return new Error('Server error. Please try again later.');
            } else {
                return new Error(extractErrorMessage(axiosError) || 'API request failed');
            }
        }

        return error instanceof Error ? error : new Error('An unknown error occurred');
    }
}

/**
 * Attach authentication interceptors to an Axios instance.
 * - Request interceptor: always injects a fresh token via acquireTokenSilent/direct token.
 * - Response interceptor: on 401, retries once with a fresh token.
 */
export function attachAuthInterceptors(api: AxiosInstance) {
    // Avoid attaching multiple times
    const marker = '__hasAuthInterceptors';
    if ((api as any)[marker]) {
        return;
    }
    (api as any)[marker] = true;

    api.interceptors.request.use(async (config) => {
        const token = await ScimAuthService.getAuthToken();
        if (config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest: any = error.config;
            const status = error?.response?.status;

            if (status === 401 && !originalRequest?._retry) {
                originalRequest._retry = true;
                try {
                    const token = await ScimAuthService.getAuthToken();
                    if (originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                    }
                    return api(originalRequest);
                } catch (retryError) {
                    // Fall through to the original error handling
                }
            }
            return Promise.reject(error);
        }
    );
}
